import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import axiosInstance from '../utils/axiosConfig';
import { getCurrentUserId } from '../utils/auth';
import { parseMongoDate } from '../utils/dateUtils.js';  // Import date utilities
import LoadingSpinner from './LoadingSpinner';
import '../styles/theme.css';
import '../styles/SpecialityInstitutionManager.css';
import '../styles/ModernMultiselect.css';
import '../styles/StudentAttendanceFormModern.css';
import 'react-calendar/dist/Calendar.css';

const StudentAttendanceFormModern = () => {
    const [selectedDates, setSelectedDates] = useState([]);
    const [timeRanges, setTimeRanges] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Curriculum and schedule data
    const [studentData, setStudentData] = useState(null);
    const [scheduleData, setScheduleData] = useState(null);
    const [currentCurriculum, setCurrentCurriculum] = useState(null);
    const [availableRotations, setAvailableRotations] = useState([]);
    const [selectedRotation, setSelectedRotation] = useState('');
    const [rotationDateRange, setRotationDateRange] = useState({ start: null, end: null });
    
    // Default hours from curriculum config
    const [defaultHours, setDefaultHours] = useState({
        fullTime: { start_time: '09:00', end_time: '17:00' },
        partTime: { start_time: '09:00', end_time: '13:00' }
    });
    const [workingMode, setWorkingMode] = useState('fullTime'); // 'fullTime' or 'partTime'

    useEffect(() => {
        fetchStudentData();
    }, []);

    const fetchStudentData = async () => {
        try {
            setLoading(true);
            
            // Fetch student data including current curriculum
            const studentResponse = await axiosInstance.get("/auth/student/dashboard");
            const student = studentResponse.data.data;
            setStudentData(student);
            
            if (student.currentCurriculum) {
                setCurrentCurriculum(student.currentCurriculum);
                
                // Fetch curriculum configuration to get default hours
                try {
                    const configResponse = await axiosInstance.get(`/config/get_curriculum_config/${student.currentCurriculum._id}`);
                    const config = configResponse.data;
                    
                    if (config.defaultFullTimeHrs && config.defaultPartTimeHrs) {
                        setDefaultHours({
                            fullTime: config.defaultFullTimeHrs,
                            partTime: config.defaultPartTimeHrs
                        });
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Default hours loaded:', config.defaultFullTimeHrs, config.defaultPartTimeHrs);
                    }
                } catch (configError) {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Could not fetch curriculum config for default hours:', configError);
                }
                
                // Fetch schedule data for the current curriculum
                try {
                    const scheduleResponse = await axiosInstance.get(`/schedules/get_curriculum_schedule/${student.currentCurriculum._id}`);
                    const schedule = scheduleResponse.data;
                    setScheduleData(schedule);
                    
                    // Extract rotations from the schedule
                    if (schedule && schedule.rotations) {
                        const rotations = schedule.rotations.map((rotation, index) => ({
                            id: index + 1,
                            number: index + 1,
                            startDate: rotation.start_date,
                            endDate: rotation.end_date,
                            ...rotation
                        }));
                        setAvailableRotations(rotations);
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Available rotations:', rotations);
                    }
                } catch (scheduleError) {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Could not fetch schedule data:', scheduleError);
                    // Fallback to old rotation fetching method
                    await fetchRotationsFallback();
                }
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('No current curriculum found for student');
                await fetchRotationsFallback();
            }
            
        } catch (err) {
            console.error('Error fetching student data:', err);
            setError('Failed to load student data. Please try again.');
            await fetchRotationsFallback();
        } finally {
            setLoading(false);
        }
    };

    const fetchRotationsFallback = async () => {
        try {
            const now = new Date();
            const currentMonth = now.getMonth(); // 0-11
            const currentYear = now.getFullYear();
            
            const curriculumYear = currentMonth < 8 ? currentYear - 1 : currentYear;

            const response = await axiosInstance.get(`/config/get_rotation_dates/${curriculumYear}`);
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Fallback rotation response:', response.data);
            
            const rotationData = response.data.rotations || response.data || [];
            const rotations = Array.isArray(rotationData) ? rotationData.map((rotation, index) => ({
                id: index + 1,
                number: index + 1,
                startDate: rotation.startDate,
                endDate: rotation.endDate,
                ...rotation
            })) : [];
            
            setAvailableRotations(rotations);
        } catch (err) {
            console.error('Fallback rotation fetch error:', err);
            setError('Failed to fetch rotation dates');
            setAvailableRotations([]);
        }
    };

    const handleDateChange = (date) => {
        // If the date is already selected, remove it
        if (selectedDates.some(d => d.toISOString().split('T')[0] === date.toISOString().split('T')[0])) {
            const newDates = selectedDates.filter(d => 
                d.toISOString().split('T')[0] !== date.toISOString().split('T')[0]
            );
            setSelectedDates(newDates);
            
            // Remove time range for deselected date
            const newTimeRanges = { ...timeRanges };
            delete newTimeRanges[date.toISOString().split('T')[0]];
            setTimeRanges(newTimeRanges);
        } else {
            // Add the new date
            const newDates = [...selectedDates, date];
            setSelectedDates(newDates);
            
            // Initialize time range for the new date with default hours
            const dateStr = date.toISOString().split('T')[0];
            const currentDefaults = defaultHours[workingMode];
            setTimeRanges(prev => ({
                ...prev,
                [dateStr]: {
                    startTime: currentDefaults.start_time,
                    endTime: currentDefaults.end_time
                }
            }));
        }
    };

    const handleTimeChange = (date, type, value) => {
        const dateStr = date.toISOString().split('T')[0];
        setTimeRanges(prev => ({
            ...prev,
            [dateStr]: {
                ...prev[dateStr],
                [type]: value
            }
        }));
    };

    const handleWorkingModeChange = (mode) => {
        setWorkingMode(mode);
        
        // Update all existing time ranges with new defaults
        const currentDefaults = defaultHours[mode];
        const updatedTimeRanges = {};
        
        selectedDates.forEach(date => {
            const dateStr = date.toISOString().split('T')[0];
            updatedTimeRanges[dateStr] = {
                startTime: currentDefaults.start_time,
                endTime: currentDefaults.end_time
            };
        });
        
        setTimeRanges(updatedTimeRanges);
    };

    const handleRotationChange = (e) => {
        const rotationNumber = parseInt(e.target.value);
        setSelectedRotation(e.target.value);
        setSelectedDates([]); // Clear selected dates when rotation changes
        setTimeRanges({}); // Clear time ranges
        
        if (rotationNumber > 0 && availableRotations.length >= rotationNumber) {
            const rotation = availableRotations[rotationNumber - 1];
            
            // Use parseMongoDate to handle both old and new date formats
            const startDate = parseMongoDate(rotation.startDate);
            const endDate = parseMongoDate(rotation.endDate);
            
            // Ensure proper date object creation
            if (!startDate || !endDate) {
                console.error('Invalid date format:', rotation.startDate, rotation.endDate);
                setRotationDateRange({ start: null, end: null });
                return;
            }
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Setting date range:', { start: startDate, end: endDate });
            setRotationDateRange({
                start: startDate,
                end: endDate
            });
        } else {
            setRotationDateRange({ start: null, end: null });
        }
    };

    const isWithinRotationRange = (date) => {
        if (!rotationDateRange.start || !rotationDateRange.end) return false;
        
        // Create date objects with time set to midnight for comparison
        const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const start = new Date(
            rotationDateRange.start.getFullYear(),
            rotationDateRange.start.getMonth(),
            rotationDateRange.start.getDate()
        );
        const end = new Date(
            rotationDateRange.end.getFullYear(),
            rotationDateRange.end.getMonth(),
            rotationDateRange.end.getDate()
        );
        
        return dateToCheck >= start && dateToCheck <= end;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const date_ranges = selectedDates.map(date => {
                const dateStr = date.toISOString().split('T')[0];
                const timeRange = timeRanges[dateStr] || { 
                    startTime: defaultHours[workingMode].start_time, 
                    endTime: defaultHours[workingMode].end_time 
                };
                return {
                    "start_date": `${dateStr}T${timeRange.startTime}:00`,
                    "end_date": `${dateStr}T${timeRange.endTime}:00`
                };
            });

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Submitting attendance payload:', { date_ranges, rotation: selectedRotation });

            await axiosInstance.post(
                `/crud/send_attendance_request/?rotation_number=${selectedRotation}`, 
                date_ranges,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            setSuccess('Attendance request submitted successfully!');
            setSelectedDates([]);
            setTimeRanges({});
            setSelectedRotation('');
            setRotationDateRange({ start: null, end: null });
        } catch (err) {
            const errorMessage = err.response?.data?.detail;
            setError(typeof errorMessage === 'object' ? 
                Object.values(errorMessage).join(', ') : 
                errorMessage || 'Failed to submit attendance request'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleFillWeekdays = () => {
        if (!rotationDateRange.start || !rotationDateRange.end) return;
        
        const weekdays = [];
        const currentDate = new Date(rotationDateRange.start);
        const endDate = new Date(rotationDateRange.end);
        
        while (currentDate <= endDate) {
            const dayOfWeek = currentDate.getDay();
            // Skip weekends (0 = Sunday, 6 = Saturday)
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                weekdays.push(new Date(currentDate));
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        setSelectedDates(weekdays);
        
        // Initialize time ranges for all weekdays
        const newTimeRanges = {};
        const currentDefaults = defaultHours[workingMode];
        weekdays.forEach(date => {
            const dateStr = date.toISOString().split('T')[0];
            newTimeRanges[dateStr] = {
                startTime: currentDefaults.start_time,
                endTime: currentDefaults.end_time
            };
        });
        setTimeRanges(newTimeRanges);
    };

    const handleClearSelection = () => {
        setSelectedDates([]);
        setTimeRanges({});
    };

    if (loading && !studentData) {
        return (
            <div className="page-container">
                <div className="content-box">
                    <div className="modern-schedule-header">
                        <LoadingSpinner text="Loading attendance..." variant="page" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="content-box">
                <div className="modern-schedule-header">
                    <div>
                        <h1 className="page-title">Student Attendance</h1>
                        <p className="schedule-subtitle">Submit your attendance for rotations</p>
                    </div>
                </div>

                {/* Curriculum Info Card */}
                {currentCurriculum && (
                    <div className="info-card modern-card">
                        <div className="card-header">
                            <h3 className="card-title">Current Curriculum</h3>
                        </div>
                        <div className="card-content">
                            <div className="curriculum-info-grid">
                                <div className="info-item">
                                    <span className="info-label">Name:</span>
                                    <span className="info-value">{currentCurriculum.name}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Rank:</span>
                                    <span className="info-value curriculum-rank">{currentCurriculum.rank}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Duration:</span>
                                    <span className="info-value">
                                        {new Date(currentCurriculum.start_date).getFullYear()} - {new Date(currentCurriculum.end_date).getFullYear()}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Available Rotations:</span>
                                    <span className="info-value">{availableRotations.length}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modern-form-container">
                    {error && <div className="status-error">{error}</div>}
                    {success && <div className="status-success">{success}</div>}
                    
                    <form onSubmit={handleSubmit} className="attendance-form">
                        {/* Rotation Selection */}
                        <div className="form-section modern-card">
                            <div className="card-header">
                                <h3 className="card-title">Select Rotation</h3>
                            </div>
                            <div className="card-content">
                                <div className="form-group">
                                    <label htmlFor="rotation" className="form-label">Choose Rotation Period</label>
                                    <select
                                        id="rotation"
                                        value={selectedRotation}
                                        onChange={handleRotationChange}
                                        className="form-select modern-input"
                                        required
                                    >
                                        <option value="">Select a rotation...</option>
                                        {availableRotations.length > 0 ? (
                                            availableRotations.map((rotation) => {
                                                const startDate = parseMongoDate(rotation.startDate);
                                                const endDate = parseMongoDate(rotation.endDate);
                                                return (
                                                    <option key={rotation.number} value={rotation.number}>
                                                        Rotation {rotation.number} - {startDate?.toLocaleDateString('en-GB') || 'Invalid'} to {endDate?.toLocaleDateString('en-GB') || 'Invalid'}
                                                    </option>
                                                );
                                            })
                                        ) : (
                                            <option value="" disabled>No rotations available</option>
                                        )}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Working Mode Selection */}
                        {selectedRotation && (
                            <div className="form-section modern-card">
                                <div className="card-header">
                                    <h3 className="card-title">Working Hours Preference</h3>
                                </div>
                                <div className="card-content">
                                    <div className="working-mode-selector">
                                        <label className="mode-option">
                                            <input
                                                type="radio"
                                                name="workingMode"
                                                value="fullTime"
                                                checked={workingMode === 'fullTime'}
                                                onChange={(e) => handleWorkingModeChange(e.target.value)}
                                                className="mode-radio"
                                            />
                                            <div className="mode-card">
                                                <div className="mode-title">Full Time</div>
                                                <div className="mode-hours">
                                                    {defaultHours.fullTime.start_time} - {defaultHours.fullTime.end_time}
                                                </div>
                                            </div>
                                        </label>
                                        <label className="mode-option">
                                            <input
                                                type="radio"
                                                name="workingMode"
                                                value="partTime"
                                                checked={workingMode === 'partTime'}
                                                onChange={(e) => handleWorkingModeChange(e.target.value)}
                                                className="mode-radio"
                                            />
                                            <div className="mode-card">
                                                <div className="mode-title">Part Time</div>
                                                <div className="mode-hours">
                                                    {defaultHours.partTime.start_time} - {defaultHours.partTime.end_time}
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                    <p className="mode-description">
                                        Default hours will be applied to selected dates. You can customize individual days later.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Calendar Selection */}
                        {selectedRotation && (
                            <div className="form-section modern-card">
                                <div className="card-header">
                                    <h3 className="card-title">Select Attendance Dates</h3>
                                </div>
                                <div className="card-content">
                                    <div className="calendar-container">
                                        <div className="calendar-actions">
                                            <button
                                                type="button"
                                                onClick={handleFillWeekdays}
                                                className="action-btn secondary"
                                                disabled={!rotationDateRange.start || !rotationDateRange.end}
                                            >
                                                📅 Auto-Fill Weekdays
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleClearSelection}
                                                className="action-btn outline"
                                                disabled={selectedDates.length === 0}
                                            >
                                                🗑️ Clear Selection
                                            </button>
                                        </div>
                                        <Calendar
                                            onChange={handleDateChange}
                                            value={selectedDates}
                                            selectRange={false}
                                            className="modern-calendar"
                                            minDate={rotationDateRange.start}
                                            maxDate={rotationDateRange.end}
                                            activeStartDate={rotationDateRange.start}
                                            tileDisabled={({ date }) => !isWithinRotationRange(date)}
                                            tileClassName={({ date }) => {
                                                return selectedDates.some(selectedDate => 
                                                    selectedDate.toISOString().split('T')[0] === date.toISOString().split('T')[0]
                                                ) ? 'selected-date' : '';
                                            }}
                                        />
                                    </div>
                                    {selectedDates.length > 0 && (
                                        <div className="selected-dates-summary">
                                            <h4>Selected Dates: {selectedDates.length}</h4>
                                            <div className="dates-list">
                                                {selectedDates.map(date => (
                                                    <span key={date.toISOString()} className="date-chip">
                                                        {date.toLocaleDateString()}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Time Ranges Customization */}
                        {selectedDates.length > 0 && (
                            <div className="form-section modern-card">
                                <div className="card-header">
                                    <h3 className="card-title">Customize Hours (Optional)</h3>
                                </div>
                                <div className="card-content">
                                    <div className="time-ranges-container">
                                        {selectedDates.map(date => {
                                            const dateStr = date.toISOString().split('T')[0];
                                            const timeRange = timeRanges[dateStr] || {
                                                startTime: defaultHours[workingMode].start_time,
                                                endTime: defaultHours[workingMode].end_time
                                            };
                                            return (
                                                <div key={dateStr} className="time-range-item">
                                                    <h4 className="date-title">{date.toLocaleDateString()}</h4>
                                                    <div className="time-inputs form-row">
                                                        <div className="time-input-group">
                                                            <label className="form-label">Start Time</label>
                                                            <input
                                                                type="time"
                                                                value={timeRange.startTime}
                                                                onChange={(e) => handleTimeChange(date, 'startTime', e.target.value)}
                                                                className="modern-input"
                                                                required
                                                            />
                                                        </div>
                                                        <div className="time-input-group">
                                                            <label className="form-label">End Time</label>
                                                            <input
                                                                type="time"
                                                                value={timeRange.endTime}
                                                                onChange={(e) => handleTimeChange(date, 'endTime', e.target.value)}
                                                                className="modern-input"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Submit Section */}
                        <div className="save-section">
                            <button 
                                type="submit" 
                                className="submit-btn primary"
                                disabled={loading || selectedDates.length === 0 || !selectedRotation}
                            >
                                {loading ? 'Submitting...' : 'Submit Attendance Request'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default StudentAttendanceFormModern;
